import { Component, Object3D } from '@wonderlandengine/api';
import { property } from '@wonderlandengine/api/decorators.js';
import Logger from './utils/logger.js';

const MODULE = 'LazyWidgetsRootCompat';

type RootLike = {
    enabled?: boolean;
    meshObject?: { active?: boolean };
    meshComponent?: { active?: boolean };
    collision?: { active?: boolean; group?: number };
    cursorTarget?: { active?: boolean };
    _testRayDirection?: (cursor: unknown) => boolean;
    _lwCompatRayPatched?: boolean;
};

type LazyWidgetsComponentLike = {
    root?: RootLike | null;
};

/**
 * Runtime compatibility shim for lazy-widgets-wle roots.
 *
 * Attach this to the same object as a lazy-widgets component (for example,
 * virtual-keyboard-ui-root). It patches collision group and optional
 * ray-direction filtering so custom cursor setups can hit the UI reliably.
 */
export class LazyWidgetsRootCompat extends Component {
    static TypeName = 'lazy-widgets-root-compat';

    @property.object()
    targetObject: Object3D | null = null;

    @property.string('virtual-keyboard-ui-root')
    targetComponentName = 'virtual-keyboard-ui-root';

    @property.int(2)
    configuredCollisionMask = 2;

    @property.bool(true)
    patchRayDirection = true;

    @property.bool(true)
    forceActiveState = true;

    private targetComponent: LazyWidgetsComponentLike | null = null;
    private loggedReady = false;

    start(): void {
        this.resolveTargetComponent();
    }

    update(_dt: number): void {
        if (!this.targetComponent) {
            this.resolveTargetComponent();
            if (!this.targetComponent) {
                return;
            }
        }

        const root = this.targetComponent.root;
        if (!root) {
            return;
        }

        const effectiveMask = this.computeEffectiveMask(this.configuredCollisionMask);

        if (root.collision) {
            root.collision.group = effectiveMask;
            if (this.forceActiveState) {
                root.collision.active = !!root.enabled;
            }
        }

        if (this.forceActiveState) {
            if (root.meshObject) {
                root.meshObject.active = !!root.enabled;
            }
            if (root.meshComponent) {
                root.meshComponent.active = !!root.enabled;
            }
            if (root.cursorTarget) {
                root.cursorTarget.active = !!root.enabled;
            }
        }

        if (this.patchRayDirection && !root._lwCompatRayPatched && typeof root._testRayDirection === 'function') {
            root._testRayDirection = () => true;
            root._lwCompatRayPatched = true;
            Logger.warn(MODULE, `Patched ray-direction check for ${this.targetComponentName}`);
        }

        if (!this.loggedReady) {
            this.loggedReady = true;
            Logger.warn(
                MODULE,
                `Applied compatibility patch to ${this.targetComponentName} with collision group ${effectiveMask} (configured ${this.configuredCollisionMask})`,
            );
        }
    }

    private resolveTargetComponent(): void {
        const host = this.targetObject ?? this.object;
        const target = host.getComponent(this.targetComponentName as never) as unknown as LazyWidgetsComponentLike | null;

        if (!target) {
            return;
        }

        this.targetComponent = target;
    }

    private computeEffectiveMask(sourceMask: number): number {
        if (sourceMask <= 0) {
            return 2;
        }

        const indexLikeMask = sourceMask < 31 ? (1 << sourceMask) : sourceMask;
        return sourceMask | indexLikeMask;
    }
}
